import { alpha } from "@material-ui/core/styles";
import { Overrides } from "@material-ui/core/styles/overrides";

import { buttonBaseStyle, buttonSizeStyles, buttonToneStyles } from "./buttonStyles";
import { fontFamilies, layoutTokens, semanticColors as colors } from "./designTokens";

const autofill = (background: string, color: string) => ({
  WebkitBoxShadow: `0 0 0 100px ${background} inset`,
  WebkitTextFillColor: color,
  caretColor: color,
  borderRadius: layoutTokens.radius.md,
});

// このモジュールはCapacitorからも読み込まれるため、React部品や@/の別名をimportしない。
export const componentOverrides: Overrides = {
  MuiButton: {
    root: {
      "&&": { ...buttonBaseStyle, ...buttonSizeStyles.M },
      "&.Mui-focusVisible": { outline: `3px solid ${alpha(colors["border/focus"], 0.18)}`, outlineOffset: 2 },
    },
    text: { "&&": buttonToneStyles.text },
    textPrimary: { "&&": buttonToneStyles.text },
    textSecondary: { "&&": buttonToneStyles.text },
    contained: { "&&": buttonToneStyles.primary },
    containedPrimary: { "&&": buttonToneStyles.primary },
    containedSecondary: { "&&": buttonToneStyles.trust },
    outlined: { "&&": buttonToneStyles.outline },
    outlinedPrimary: { "&&": buttonToneStyles.outline },
    outlinedSecondary: { "&&": buttonToneStyles.outline },
    sizeLarge: { "&&": buttonSizeStyles.L },
    sizeSmall: { "&&": buttonSizeStyles.S },
  },
  MuiOutlinedInput: {
    root: {
      "&&": {
        height: 48,
        borderRadius: layoutTokens.radius.md,
        backgroundColor: colors["field/bg"],
        color: colors["text/primary"],
        fontSize: 14,
        lineHeight: 1.5,
        "& .MuiOutlinedInput-notchedOutline": { borderWidth: 1.5, borderColor: "transparent" },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
        "&.Mui-focused": { boxShadow: `0 0 0 3px ${alpha(colors["border/focus"], 0.18)}` },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors["border/focus"], borderWidth: 1.5 },
        "&.Mui-error": {
          backgroundColor: colors["status/error-bg"],
          "& .MuiOutlinedInput-notchedOutline": { borderColor: colors["status/error"], borderWidth: 1.5 },
          "&.Mui-focused": { boxShadow: `0 0 0 3px ${alpha(colors["status/error"], 0.18)}` },
          "& input:-webkit-autofill": autofill(colors["status/error-bg"], colors["text/primary"]),
        },
        "&.Mui-disabled": {
          backgroundColor: colors["field/bg-disabled"],
          color: colors["text/disabled"],
          boxShadow: "none",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
          "& input:-webkit-autofill": autofill(colors["field/bg-disabled"], colors["text/disabled"]),
        },
        "&.MuiOutlinedInput-multiline": { height: "auto", minHeight: 48, padding: "14px 16px" },
        // 値だけを表示するreadOnly。日付など末尾に選択操作を持つ入力は塗りを維持する。
        "&:has(> input[readonly]):not(.MuiInputBase-adornedEnd)": {
          height: "auto",
          backgroundColor: "transparent",
          boxShadow: "none",
          "& .MuiOutlinedInput-notchedOutline": { display: "none" },
          "& input": { padding: 0 },
        },
      },
    },
    input: {
      padding: "13.5px 16px",
      "&::placeholder": { color: colors["text/placeholder"], opacity: 1 },
      "&:-webkit-autofill": autofill(colors["field/bg"], colors["text/primary"]),
      "&.Mui-disabled::placeholder": { color: colors["text/disabled"] },
    },
    inputMultiline: { padding: 0 },
    inputMarginDense: { paddingTop: 13.5, paddingBottom: 13.5 },
  },
  MuiInputLabel: { root: { fontSize: 12, fontWeight: 700, color: colors["text/primary"] } },
  MuiFormHelperText: {
    root: { fontSize: 12, lineHeight: 1.5, minHeight: 18 },
    contained: { marginLeft: 0, marginRight: 0 },
  },
  MuiSelect: {
    select: { "&:focus": { backgroundColor: "transparent", borderRadius: layoutTokens.radius.md } },
    icon: { color: colors["icon/default"] },
  },
  MuiCheckbox: {
    root: {
      color: colors["border/strong"],
      padding: 12,
      "& .MuiSvgIcon-root": { fontSize: 20 },
      "&.Mui-checked": { color: colors["action/primary"] },
      "&&.Mui-disabled": { color: colors["border/default"] },
    },
    colorPrimary: { "&.Mui-checked": { color: colors["action/primary"] } },
    colorSecondary: { "&.Mui-checked": { color: colors["action/primary"] } },
  },
  MuiRadio: {
    root: {
      color: colors["border/strong"],
      padding: 12,
      "& .MuiSvgIcon-root": { fontSize: 20 },
      "&.Mui-checked": { color: colors["action/primary"] },
      "&&.Mui-disabled": { color: colors["border/default"] },
    },
    colorPrimary: { "&.Mui-checked": { color: colors["action/primary"] } },
    colorSecondary: { "&.Mui-checked": { color: colors["action/primary"] } },
  },
  MuiFormControlLabel: {
    label: { fontSize: 14, lineHeight: 1.5, fontWeight: 400, "&.Mui-disabled": { color: colors["text/disabled"] } },
  },
  MuiChip: {
    root: {
      backgroundColor: colors["surface/card"],
      border: `1px solid ${colors["border/strong"]}`,
      height: 32,
      borderRadius: layoutTokens.radius.full,
      fontSize: 12,
      fontWeight: 500,
      color: colors["text/secondary"],
      "&.Mui-disabled": {
        opacity: 1,
        backgroundColor: colors["surface/subtle"],
        borderColor: colors["border/default"],
        color: colors["text/disabled"],
      },
    },
    label: { paddingLeft: 16, paddingRight: 16 },
    colorPrimary: {
      backgroundColor: colors["state/selected-bg"],
      borderColor: colors["action/primary"],
      color: colors["action/primary"],
    },
    outlinedPrimary: {
      backgroundColor: colors["surface/card"],
      borderColor: colors["border/strong"],
      color: colors["text/secondary"],
    },
  },
  MuiSwitch: {
    root: { width: 64, height: 44, padding: 10 },
    switchBase: {
      padding: 12,
      color: colors["surface/card"],
      "&.Mui-checked": {
        transform: "translateX(20px)",
        color: colors["surface/card"],
        "& + .MuiSwitch-track": { backgroundColor: colors["action/primary"], opacity: 1 },
      },
      "&&.Mui-disabled": {
        color: colors["surface/subtle"],
        "& + .MuiSwitch-track": { backgroundColor: colors["border/default"], opacity: 1 },
      },
    },
    thumb: { width: 20, height: 20 },
    track: { borderRadius: layoutTokens.radius.full, backgroundColor: colors["border/strong"], opacity: 1 },
    colorPrimary: { "&.Mui-checked": { color: colors["surface/card"] } },
    colorSecondary: { "&.Mui-checked": { color: colors["surface/card"] } },
  },
  MuiTab: {
    root: {
      textTransform: "none",
      fontSize: 14,
      fontWeight: 500,
      lineHeight: 1.3,
      color: colors["text/secondary"],
      "&.Mui-selected": { color: colors["text/primary"], fontWeight: 700 },
    },
    textColorPrimary: { "&.Mui-selected": { color: colors["text/primary"] } },
  },
  MuiTabs: { indicator: { height: 2, backgroundColor: colors["action/primary"] } },
  MuiAvatar: {
    root: { width: 44, height: 44 },
    colorDefault: { backgroundColor: colors["state/selected-bg"], color: colors["action/primary"] },
  },
  MuiBadge: {
    badge: {
      fontFamily: fontFamilies.number,
      fontSize: 10,
      fontWeight: 600,
      height: 16,
      minWidth: 16,
      padding: "0 4px",
    },
  },
  MuiLinearProgress: {
    root: { height: 4, borderRadius: 2 },
    colorPrimary: { backgroundColor: colors["border/default"] },
    barColorPrimary: { backgroundColor: colors["action/primary"] },
    bar: { borderRadius: 2 },
  },
  MuiDivider: { root: { backgroundColor: colors["border/default"] } },
};
